<template>
  <div class="OperatorTagFilterList">
    <div class="inputgroup">
        <UIInput
        :title="$t('Search for a Tag')"
        :placeholder="$t('Search for a Tag')"
        type="search" v-model="search" />
    </div>
    <div class="inputgroup">
        <UISelect
          :title="$t('Title needs ... ')"
          v-model="localStation.operatorTagsRequireAll"
          class="operatorTagsRequireAll"
          :instructions="$t('This will only show the customer content that either satisfies all tags (AND) or at least one of the tags (OR)')"
        >
            <option v-bind:value="true">{{ $t('Title needs all of these tags') }}</option>
            <option v-bind:value="false">{{ $t('Title needs at least one of these tags') }}</option>
        </UISelect>
    </div>
    <div class="inputgroup searchtable">
      <div class="tableheader f6 storm">
        <div class="mobile">{{ $t('Enabled Tag on Station') }}</div>
        <div class="tablet">{{ $t('Enabled Tag on Station') }}</div>
      </div>
      <template v-if="tagResults.length > 0">
        <!-- individual tag entries -->
        <div class="tagslist" v-for="tag in tagResults" :key="tag.id">
          <UISwitch
            class="tablet"
            :title="tag.text"
            :value="isTagSelected(tag)"
            @change="toggleTagOnStation(tag)"
          />
        </div>
      </template>
      <div class="empty noresults f-body tc center fill-geyser fadeIn animated" v-else>
        <UIIcon name="no-results" class="dib mv5" style="width:8rem;" />
        <p>{{ $t("We couldn't find any titles matching your search.") }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';

import UISelect from '@/core/shared/components/ui/UISelect';
import UIInput from '@/core/shared/components/ui/UIInput';
import UIIcon from '@/core/shared/components/ui/UIIcon';
import UISwitch from '@/core/shared/components/ui/UISwitch';

@Component({
  components: {
    UIInput,
    UISelect,
    UIIcon,
    UISwitch,
  },
  props: {
    station: {
      type: Object,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
  },
  watch: {
    search: {
      handler: 'onSearchChanged',
      immediate: true,
      deep: true,
    },
  },
})
export default class OperatorTagsFilterSettings extends Vue {
  search = '';
  filteredTags = [];

  get localStation () {
    return new Proxy(this.station, {
      set: (object, property, value) => {
        this.$emit('edit:station', property, value);
        return true;
      },
    });
  }

  onSearchChanged () {
    this.filteredTags = this.tags.filter(tag => tag.text.toLowerCase().includes(this.search.toLowerCase()));
  }

  get tagResults () {
    if (this.filteredTags) {
      return this.filteredTags;
    } else {
      return this.tags;
    }
  }

  isTagSelected (tag) {
    return ~this.getTagIndexInSelectedTag(tag);
  }

  getTagIndexInSelectedTag (tag) {
    return this.localStation.operatorTags.findIndex(stationTag => {
      return stationTag.id === tag.id;
    });
  }

  toggleTagOnStation (tag) {
    const tagIndex = this.getTagIndexInSelectedTag(tag);
    if (~tagIndex) {
      this.localStation.operatorTags = [
        ...this.station.operatorTags.slice(0, tagIndex),
        ...this.station.operatorTags.slice(tagIndex + 1),
      ];
    } else {
      this.localStation.operatorTags = [
        ...this.localStation.operatorTags,
        { id: tag.id },
      ];
    }
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.OperatorTagFilterList {
  display: grid;
  grid-gap: var(--spacingSm);

  @media (--phone) {
    grid-template-columns: 1;
    grid-gap: var(--spacingMd);
  }

  @media (--tablet) {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: var(--spacingMd);
  }

  @media (--desktopXL) {
    grid-template-columns: repeat(3, 1fr);
  }

  & .searchtable {

    display: grid;
    grid-gap: var(--spacingMd);

    @media (--desktopXL) {
      grid-column: span 2;
    }

    @media (--phone) {
      grid-column: span 1;
    }

    @media (--tablet) {
      grid-column: span 2;
    }

    & .tableheader,
    & .gameresult {
      display: grid;
      grid-template-columns: 1fr 5rem;
      align-items: center;

      & .tablet {
        display: none;
      }

      grid-column: span 2;

      @media (--phone) {
        grid-column: span 1;
      }
      @media (--tablet) {
        grid-gap: var(--spacingMd);
        grid-template-columns: 1fr 1fr 5rem;

        & .tablet {
          display: flex;
        }
      }
    }

    & .tableheader {
      padding-bottom: var(--spacingSm);
      border-bottom: 0.1rem solid var(--colorStorm);
    }
  }
}
</style>
